import { useEffect, useMemo, useState } from 'react'
import { Input, Modal, Select, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Rule } from 'antd/es/form'

import { QUERY_KEYS } from '@api'
import { useGetTelePhonePrefixes } from '@hooks'
import { defaultTelephonePreffix } from '@constants'
import { TelephonePrefix } from '@types'

import { useSuppliers } from '../../hooks/useSuppliers'
import CommercialSuppliersApi from '../../hooks/useCommercialSuppliers/api'
import useCreateCommercialSupplier from '../../hooks/useCreateCommercialSupplier'
import { useUpdateCommercialSupplier } from '../../hooks/useUpdateCommercialSupplier'
import { StyledForm, StyledRow } from '../StyledComponents'

type FormPayload = {
  name: string
  surname?: string
  userName?: string
  email: string
  cellphone?: string
  password?: string
  supplierIds: number[]
}

type Props = {
  open: boolean
  editingCommercialSupplierId: number | null
  onClose: () => void
  onSuccess: () => void
}

const formValidations = {
  businessName: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.businessName_format_require',
    min: 3,
    max: 20,
  },
  tradeName: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.tradeName_format_require',
    min: 3,
    max: 20,
  },
  email: { required: 'globals.obligatory_field', format: 'supplier_modal.email_format' },
  cellphone: {
    required: 'globals.obligatory_field',
    format: 'globals.cellphone_format_require',
    min: 10,
    max: 15,
  },
  password: {
    required: 'globals.obligatory_field',
    format: 'globals.password_format_require',
    min: 8,
    regex: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[@$!%*?&._])[A-Za-z0-9@$!%*?&._]{8,}$/,
  },
}

const numericRegex = /^[0-9]*$/

/** Stored cellphone is prefix digits + local digits; match longest prefix first. */
const splitStoredCellphone = (
  full: string | undefined,
  prefixes: TelephonePrefix[],
): { prefixId: number | undefined; localDigits: string } => {
  if (!full?.trim() || !prefixes.length) {
    return { prefixId: undefined, localDigits: '' }
  }
  const digits = full.replace(/\D/g, '')
  if (!digits) {
    return { prefixId: undefined, localDigits: '' }
  }
  const sorted = [...prefixes].sort((a, b) => b.prefix.length - a.prefix.length)
  for (const p of sorted) {
    if (digits.startsWith(p.prefix)) {
      return { prefixId: p.id, localDigits: digits.slice(p.prefix.length) }
    }
  }
  return { prefixId: undefined, localDigits: digits }
}

const AddCommercialUserModal: React.FC<Props> = ({
  open,
  editingCommercialSupplierId,
  onClose,
  onSuccess,
}) => {
  const [selectedPreffix, setSelectedPrefix] = useState<number>()
  const { t } = useTranslation()
  const { telephonePrefixes } = useGetTelePhonePrefixes()
  const [form] = StyledForm.useForm()
  const { suppliersOptions } = useSuppliers({ disabled: false })
  const isEdit = editingCommercialSupplierId !== null

  const onMutationSuccess = () => {
    onSuccess()
    form.resetFields()
    onClose()
  }

  const { createCommercialSupplier, isLoadingCreateCommercialSupplier } =
    useCreateCommercialSupplier(onMutationSuccess)
  const { updateCommercialSupplier, isLoadingUpdateCommercialSupplier } =
    useUpdateCommercialSupplier(onMutationSuccess)

  const { data: detail, isLoading: detailLoading } = useQuery(
    [QUERY_KEYS.COMMERCIAL_SUPPLIER_DETAIL, editingCommercialSupplierId],
    () => CommercialSuppliersApi.getCommercialSupplierById(editingCommercialSupplierId!),
    {
      enabled: open && isEdit,
      staleTime: 0,
    },
  )

  useEffect(() => {
    if (!open) return
    if (!isEdit) {
      form.resetFields()
      form.setFieldsValue({ supplierIds: [] })
      return
    }
    if (!detail) return
    const cs = detail.commercialSupplier
    form.setFieldsValue({
      name: cs.name,
      surname: cs.surname,
      email: cs.email,
      supplierIds: cs.suppliers?.map(s => s.id) ?? [],
    })
  }, [open, isEdit, detail, form])

  useEffect(() => {
    if (!open) {
      setSelectedPrefix(undefined)
    }
  }, [open])

  useEffect(() => {
    if (!open || isEdit || !telephonePrefixes?.length) return
    if (!selectedPreffix) {
      const defaultPreffix = telephonePrefixes.find(
        ({ prefix }) => prefix === defaultTelephonePreffix,
      )
      setSelectedPrefix(defaultPreffix ? defaultPreffix.id : telephonePrefixes[0]?.id)
    }
  }, [open, isEdit, telephonePrefixes, selectedPreffix])

  useEffect(() => {
    if (!open || !isEdit || !detail || !telephonePrefixes?.length) return

    const cs = detail.commercialSupplier
    const { prefixId, localDigits } = splitStoredCellphone(cs.cellphone, telephonePrefixes)

    if (prefixId !== undefined) {
      setSelectedPrefix(prefixId)
      form.setFieldValue('cellphone', localDigits)
      return
    }

    const fromCountry = cs.country
      ? telephonePrefixes.find(({ countryShortName }) => countryShortName === cs.country)
      : undefined
    setSelectedPrefix(fromCountry?.id ?? telephonePrefixes[0]?.id)
    form.setFieldValue('cellphone', localDigits)
  }, [open, isEdit, detail, telephonePrefixes, form])

  const supplierSelectOptions = useMemo(() => {
    const base = suppliersOptions ?? []
    const byId = new Map(base.map(o => [o.value as number, o]))

    const assignedDisabled =
      detail?.commercialSupplier?.suppliers?.filter(
        s => s.disableAt !== null && s.disableAt !== undefined,
      ) ?? []
    const disabledSuffix = ` (${t('globals.generics.disabled').toLowerCase()})`

    for (const s of assignedDisabled) {
      if (!byId.has(s.id)) {
        byId.set(s.id, {
          label: `${s.businessName}${disabledSuffix}`,
          value: s.id,
        })
      }
    }

    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
  }, [suppliersOptions, detail, t])

  const modalTitle = isEdit ? t('commercial_users.edit') : t('commercial_users.add')

  const showFormSpinner = isEdit && detailLoading
  interface ValidationRule {
    validator: (_rule: Rule, value: string) => Promise<void>
  }
  const validateAlphanumeric: ValidationRule['validator'] = (_rule, value) => {
    if (!value || /^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s]+$/.test(value)) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(t('supplier_modal.format_require')))
  }

  const buildStoredCellphone = (localDigits: string | undefined) => {
    if (localDigits === undefined || localDigits === '') return localDigits
    const prefixRecord = telephonePrefixes.find(p => p.id === selectedPreffix)
    return prefixRecord ? `${prefixRecord.prefix}${localDigits}` : localDigits
  }

  const handleFinish = (v: FormPayload & { confirmPassword?: string }) => {
    const { confirmPassword: _c, name, surname, userName, password, ...rest } = v
    const cellphone = buildStoredCellphone(rest.cellphone)
    if (isEdit && editingCommercialSupplierId !== null) {
      updateCommercialSupplier(editingCommercialSupplierId, {
        name,
        surname,
        email: rest.email,
        cellphone,
        supplierIds: rest.supplierIds,
      })
      return
    }
    createCommercialSupplier({
      name,
      surname: surname!,
      userName: userName!,
      email: rest.email,
      cellphone,
      supplierIds: rest.supplierIds,
      password: password!,
    })
  }

  return (
    <Modal
      open={open}
      title={modalTitle}
      okText={t('commercial_users.save')}
      cancelText={t('commercial_users.cancel')}
      confirmLoading={
        isEdit ? isLoadingUpdateCommercialSupplier : isLoadingCreateCommercialSupplier
      }
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Spin spinning={showFormSpinner}>
        <StyledForm
          form={form}
          onFinish={v => handleFinish(v as FormPayload & { confirmPassword?: string })}
          initialValues={{ supplierIds: [] }}
        >
          <StyledRow className="carrier-form-row">
            <StyledForm.Item
              name="name"
              label={t('commercial_users.name')}
              rules={[
                { required: true, message: t('globals.obligatory_field') },
                { validator: validateAlphanumeric },
              ]}
            >
              <Input disabled={showFormSpinner} />
            </StyledForm.Item>
          </StyledRow>
          <StyledRow className="carrier-form-row">
            <StyledForm.Item
              name="surname"
              label={t('commercial_users.surname')}
              rules={[{ required: true, message: t('globals.obligatory_field') }]}
            >
              <Input disabled={showFormSpinner} />
            </StyledForm.Item>
          </StyledRow>
          {!isEdit && (
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="userName"
                label={t('commercial_users.user_name')}
                rules={[
                  { required: true, message: t('globals.obligatory_field') },
                  { validator: validateAlphanumeric },
                ]}
              >
                <Input />
              </StyledForm.Item>
            </StyledRow>
          )}
          <StyledRow className="carrier-form-row">
            <StyledForm.Item
              name="email"
              label={t('commercial_users.email')}
              rules={[{ required: true, type: 'email', message: t('globals.obligatory_field') }]}
            >
              <Input type="email" disabled={showFormSpinner} />
            </StyledForm.Item>
          </StyledRow>
          <StyledRow className="carrier-form-row">
            <StyledForm.Item
              name="cellphone"
              label={t('commercial_users.cellphone')}
              rules={[
                {
                  validator: (_, value) => {
                    if (value) {
                      if (!numericRegex.test(value)) {
                        return Promise.reject(t(formValidations.cellphone.format))
                      }
                      if (
                        value.length < formValidations.cellphone.min ||
                        value.length > formValidations.cellphone.max
                      ) {
                        return Promise.reject(
                          t(formValidations.cellphone.format, {
                            min: formValidations.cellphone.min,
                            max: formValidations.cellphone.max,
                          }),
                        )
                      }
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Input
                disabled={showFormSpinner}
                addonBefore={
                  <Select
                    value={selectedPreffix}
                    style={{ width: 100 }}
                    onChange={value => setSelectedPrefix(value)}
                  >
                    {telephonePrefixes?.map((item: TelephonePrefix) => (
                      <Select.Option key={item.prefix} value={item.id}>
                        {`+${item.prefix}${item.prefix === '54' ? ' (AR)' : ''}`}
                      </Select.Option>
                    ))}
                  </Select>
                }
                onChange={e => {
                  const enteredValue = e.target.value
                  form.setFieldValue('cellphone', enteredValue)
                }}
              />
            </StyledForm.Item>
          </StyledRow>
          <StyledRow className="carrier-form-row">
            <StyledForm.Item
              name="supplierIds"
              label={t('commercial_users.suppliers')}
              rules={[{ required: true, message: t('globals.obligatory_field') }]}
            >
              <Select
                mode="multiple"
                options={supplierSelectOptions}
                optionFilterProp="label"
                disabled={showFormSpinner}
              />
            </StyledForm.Item>
          </StyledRow>
          <StyledRow className="carrier-form-row">
            {!isEdit && (
              <>
                <StyledForm.Item
                  name="password"
                  label={t('commercial_users.password')}
                  rules={[
                    { required: true, message: t('globals.obligatory_field') },
                    {
                      min: formValidations.password.min,
                      message: t('globals.password_min_length_error', {
                        min: formValidations.password.min,
                      }),
                    },
                    {
                      pattern: formValidations.password.regex,
                      message: t(formValidations.password.format),
                    },
                  ]}
                >
                  <Input.Password />
                </StyledForm.Item>
                <StyledForm.Item
                  name="confirmPassword"
                  label={t('commercial_users.confirm_password')}
                  dependencies={['password']}
                  rules={[
                    { required: true, message: t('globals.obligatory_field') },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) return Promise.resolve()
                        return Promise.reject(new Error('Passwords do not match'))
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </StyledForm.Item>
              </>
            )}
          </StyledRow>
        </StyledForm>
      </Spin>
    </Modal>
  )
}

export { AddCommercialUserModal }
